import { useForm, yupResolver } from "@mantine/form";
import React, { FC, memo } from "react";
import { forms } from "../../../../forms";
import {
  Button,
  FileInput,
  Grid,
  Image,
  Modal,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import CropPickerInput from "../../../../components/ImageCropPicker/CropPickerInput";
import { useAddBannerMutation } from "../../../../hooks/home-banners/mutation/useAddBanner.mutation";
import { useUpdateBannerMutation } from "../../../../hooks/home-banners/mutation/useUpdateBanner.mutation";

interface IBannerModal {
  show: boolean;
  onClose: () => void;
  refetch: () => void;
  data?: THomePageBanners;
}

const BannerModal: FC<IBannerModal> = ({ data, onClose, show, refetch }) => {
  const formHandler = useForm({
    initialValues: {
      ...forms.homeBanner.values,
      ...data,
    },
    validate: yupResolver(forms.homeBanner.validation),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const { onSubmit, getInputProps } = formHandler;

  const { mutateAsync: createBanner, isPending: createLoading } =
    useAddBannerMutation();
  const { mutateAsync: updateBanner, isPending: updateLoading } =
    useUpdateBannerMutation();

  const handleSubmit = async (values: typeof forms.homeBanner.values) => {
    const formData = new FormData();
    if (typeof values.banner !== "string") {
      formData.append("banner", values.banner, "banner.webp");
    }
    if (typeof values.mobileBanner !== "string") {
      formData.append("mobileBanner", values.mobileBanner, "mobileBanner.webp");
    }
    formData.append("title", values.title);
    formData.append("colorText", values.colorText);
    formData.append("_id", values._id);
    const res = data
      ? await updateBanner(formData)
      : await createBanner(formData);
    if (res.status === "success") {
      onClose();
      refetch();
      showNotification({ message: res.message, color: "green" });
    } else {
      showNotification({ message: res.data.message, color: "red" });
    }
  };

  return (
    <Modal
      opened={show}
      onClose={onClose}
      title={data ? "Edit Banner" : "Add Banner"}
      size={"md"}
    >
      <form onSubmit={onSubmit(handleSubmit)}>
        <Grid gutter={10}>
          <Grid.Col lg={12}>
            <TextInput {...getInputProps("title")} label="Title" />
          </Grid.Col>
          <Grid.Col lg={12}>
            <TextInput {...getInputProps("colorText")} label="Colored Text" />
          </Grid.Col>

          <Grid.Col lg={12}>
            <CropPickerInput
              name="banner"
              label="Banner (ratio: 1351 X 418)"
              formHandler={formHandler}
              pickerRatio={1351 / 418}
            />
            {formHandler.values?.banner && (
              <Image
                src={
                  typeof formHandler.values?.banner === "string"
                    ? formHandler.values?.banner
                    : URL.createObjectURL(formHandler.values?.banner)
                }
                my={10}
              />
            )}
          </Grid.Col>
          <Grid.Col lg={12}>
            <CropPickerInput
              name="mobileBanner"
              label="Mobile Banner (ratio: 1 / 1)"
              formHandler={formHandler}
              pickerRatio={1 / 1}
            />
            {formHandler.values?.mobileBanner && (
              <Image
                src={
                  typeof formHandler.values?.mobileBanner === "string"
                    ? formHandler.values?.mobileBanner
                    : URL.createObjectURL(formHandler.values?.mobileBanner)
                }
                my={10}
              />
            )}
          </Grid.Col>
          <Grid.Col lg={12}>
            <Button
              loading={createLoading || updateLoading}
              disabled={createLoading || updateLoading}
              type="submit"
              fullWidth
              variant="filled"
              color="cyan"
            >
              Submit
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
};

export default memo(BannerModal);
